import PropTypes from 'prop-types';
import { useState } from 'react';
import Html5Entities from '../../external/npm/html5-entities.js';
import { NEED_LEVEL_CSS_SELECTORS } from './MapSearchUtils.js';
import * as MapSearchUtils from './MapSearchUtils.js';
import { MapProjectCard } from './MapProjectCard.js';
import { MapEssentialsCard } from './MapEssentialsCard.js';
import { MapSchoolCard } from './MapSchoolCard.js';
import { buildHackySchoolPageUrl } from '../common/buildHackySchoolPageUrl.js'
import school from '../search/school';


// We split school map markers into individual map markers (instead of stacking them) in WS-33690 (School-level giving)
// If we ever move back to grouping them, we would want to retest the functionality. (Esp. totalCostToComplete)
export const SchoolSublist = function ({ index, projects, essentials, schoolData, isMobile, schoolDonationEnabled, hasMultipleSchools, isCollapsed, toggleIsSelected}) {
    const schoolName = schoolData.schoolName;
    const count = projects.length + essentials.length;
    const totalCostToComplete = Math.round(schoolData.totalCostToComplete);
    const freeReducedPriceLunch = schoolData.freeReducedPriceLunch;
    const equityFocus = schoolData.equityFocus;
    const level = MapSearchUtils.Helpers.getNeedLevelForDisplay(freeReducedPriceLunch);
    const [showMoreEssentials, setShowMoreEssentials] = useState(false);

    const getNeedDescription = function () {
        let description;
        if (level === NEED_LEVEL_CSS_SELECTORS[4]) {
            description = 'Nearly all students from low-income households';
        } else if (level === NEED_LEVEL_CSS_SELECTORS[3]) {
            description = 'At least three-quarters of students from low-income households';
        } else if (level === NEED_LEVEL_CSS_SELECTORS[2]) {
            description = 'At least a third of students from low-income households';
        } else {
            description = 'Under a quarter of students from low-income households';
        }
        return description;
    };

    const getSchoolCard = function () {
        // If this school was not in the school search results, then skip the additional school card
        if (schoolData.schoolId) {
            return <MapSchoolCard
                key={`school-${schoolData.schoolId}`}
                schoolData={schoolData}
                schoolDonationEnabled={schoolDonationEnabled}
            />;
        }
        return null;
    }
    const renderMoreEssentials = function(essentials) {
        if(showMoreEssentials && essentials.length > 2) {
            return (
                <div className="essentials-container">
                    { essentials.slice(2).map(essential =>
                        <MapEssentialsCard
                            key={`essential-${essential.teacherId}`}
                            essential={essential}
                        />)}
                </div>
            );
        } else if(!showMoreEssentials && essentials.length > 2) {
            return (
                <button className="view-more text-link" onClick={() => setShowMoreEssentials(true)}>View more essentials requests</button>
            );
        } else {
            return null;
        }
    }
    const getSchoolTitle = function () {
        // We search *separately* for projects, lists, and schools. It's unlikely but possible that you find a project
        // without finding its school. When that happens, we'll have a school-marker but no school-data for that school.
        // So: we have to check for the presence of schoolData here.
        if (schoolDonationEnabled && schoolData && schoolData.schoolId) {
            let landingPageUrl;
            if (schoolData && schoolData.schoolInfoPagePath) {
                landingPageUrl = baseURL + schoolData.schoolInfoPagePath;
            } else {
                landingPageUrl = buildHackySchoolPageUrl(schoolData.schoolId);
            }
            return <a target="_blank" href={landingPageUrl}> {schoolName} </a>;
        }
        return schoolName;
    }

    return (
        <div className='school' key={`school-${index}`}>
            {(index == 0 && isCollapsed) &&
                <h3>Schools at this location</h3>
            }
            {isCollapsed &&
                <button className="school-collapsed" onClick={() => toggleIsSelected(index)}>
                    <span className={'price-marker need-level ' + level}>
                        <div className='school-marker-icon'></div>
                    </span>
                    <div className="school-point-info">
                        <div>{schoolName}</div>
                        {count != 0 ?
                            <div className="subtitle">
                                {count + ' request' + (count > 1 ? 's' : '')
                                + (totalCostToComplete != 0 && Html5Entities.decode('&bull;')
                                + ' $'
                                + totalCostToComplete
                                + ' still needed')}
                            </div>
                            : ''
                        }
                    </div>
                </button>
            }
            <section className={'school-container ' +  (hasMultipleSchools && isCollapsed ? '' : 'show')}>
                { hasMultipleSchools && <div className="inline-button"><button className="button-secondary small back-button" onClick={() => toggleIsSelected(index)}>Back</button></div> }
                <h3 className='school-title'>{getSchoolTitle()}</h3>
                <div className='need-description'>
                    <span className={'price-marker need-level ' + level}><div className='school-marker-icon'></div></span>
                    {getNeedDescription()}
                </div>
                { getSchoolCard() }
                { schoolDonationEnabled && (
                    (projects.length != 0) ?
                        <div className="fine-print">Your donation will be applied to the top project at this school.</div>
                        :
                        <div className="fine-print blue">Your donation will be made available to a teacher at this school to use on a project. Project funds are then used to directly purchase materials. <a href='https://help.donorschoose.org/hc/en-us/articles/22411454874775'>Learn more about donations to schools</a></div>
                )
                }
                { essentials && (
                    <div className={"essentials-container " + (essentials.length == 1 ? 'single' : '')}>
                        { essentials.slice(0, 2).map(essential =>
                            <MapEssentialsCard
                                key={`essential-${essential.teacherId}`}
                                essential={essential}
                            />)}
                    </div>
                )
                }
                { essentials && renderMoreEssentials(essentials) }
                { projects.map(project =>
                    <MapProjectCard
                        key={`project-${project.id}`}
                        project={project}
                        equityFocus={equityFocus}
                        isMobile={isMobile}
                    />)}
                { count != 0 ? <p className='project-count'><b>{count}</b> request{count > 1 ? 's' : ''} at this school</p> : null}
            </section>
        </div>
    );
};

SchoolSublist.propTypes = {
    index: PropTypes.number.isRequired,
    projects: PropTypes.array.isRequired,
    essentials: PropTypes.array.isRequired,
    schoolData: PropTypes.object.isRequired,
    hasMultipleSchools: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool,
    isCollapsed: PropTypes.bool,
    toggleIsSelected: PropTypes.func.isRequired,
};
